import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext } from 'react'
import { BillingAccountsContext } from '~/contexts/BillingAccountsContext'
import { WorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'

// Read the billing account id directly off the context so callers that
// render outside a BillingAccountsProvider (legacy chat tests, embedded
// previews, etc.) get a clean `null` rather than the provider's
// "must be used within a Provider" throw. Combined with `enabled: !!base`
// below, the hooks degrade to a no-op in that case instead of erroring.
const useSelectedBillingAccountId = (): number | null => {
  const ctx = useContext(BillingAccountsContext)
  return ctx?.selectedBillingAccountId ?? null
}

const useCurrentWorkspaceUuid = (billingAccountId: number | null): string | null => {
  const ctx = useContext(WorkspaceContext)
  if (
    billingAccountId == null ||
    ctx?.isLoading ||
    (ctx?.billingAccountId != null && ctx.billingAccountId !== billingAccountId)
  ) {
    return null
  }
  return ctx?.currentWorkspace?.uuid ?? null
}

export type BookmarkVisibility = 'personal' | 'account'

export interface PromptBookmark {
  uuid: string
  title: string
  body: string
  visibility: BookmarkVisibility
  /**
   * Whether the calling user authored this bookmark. The backend
   * intentionally does NOT echo back the author's internal user_id —
   * `is_owner` is the only ownership signal the UI needs (gates the
   * Edit / Delete affordances on each row).
   */
  is_owner: boolean
  /** Author display name, or null if the author has been deleted. */
  author_name: string | null
  /**
   * Author email, only populated for the bookmark's own author.
   * Always null when viewed by another account member (PII gate).
   */
  author_email: string | null
  created_time: string
  updated_time: string
}

export interface ListPromptBookmarksResponse {
  bookmarks: PromptBookmark[]
  total: number
}

export interface CreatePromptBookmarkPayload {
  title: string
  body: string
  visibility: BookmarkVisibility
}

export interface UpdatePromptBookmarkPayload {
  title?: string
  body?: string
  visibility?: BookmarkVisibility
}

const getBase = (
  workspaceUuid: string | null | undefined,
  billingAccountId: number | null | undefined
) =>
  workspaceUuid && billingAccountId != null
    ? `/api/workspace/${workspaceUuid}/billing/${billingAccountId}/prompt-bookmarks`
    : null

export const PROMPT_BOOKMARK_QUERY_KEY = ['promptBookmarks'] as const

export const usePromptBookmarks = (
  visibility?: BookmarkVisibility,
  options: { enabled?: boolean } = {}
) => {
  const selectedBillingAccountId = useSelectedBillingAccountId()
  const workspaceUuid = useCurrentWorkspaceUuid(selectedBillingAccountId)
  const base = getBase(workspaceUuid, selectedBillingAccountId)
  const param = visibility ? `?visibility=${visibility}` : ''
  return useQuery<ListPromptBookmarksResponse, Error>({
    queryKey: [
      ...PROMPT_BOOKMARK_QUERY_KEY,
      workspaceUuid,
      selectedBillingAccountId,
      visibility ?? 'all'
    ],
    queryFn: () => {
      if (!base) throw new Error('Billing account not selected')
      return fetchWrapper.get<ListPromptBookmarksResponse>(`${base}${param}`)
    },
    enabled: !!base && (options.enabled ?? true)
  })
}

// Returning the invalidate Promise from `onSuccess` makes React Query
// wait for the active list-query refetch to settle before resolving
// `mutateAsync`. Without this, callers that close their modal right
// after `await mutateAsync(...)` show a stale list for one frame.
const invalidateBookmarks = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: PROMPT_BOOKMARK_QUERY_KEY })

export const useCreatePromptBookmark = () => {
  const selectedBillingAccountId = useSelectedBillingAccountId()
  const workspaceUuid = useCurrentWorkspaceUuid(selectedBillingAccountId)
  const qc = useQueryClient()
  return useMutation<PromptBookmark, Error, CreatePromptBookmarkPayload>({
    mutationFn: payload => {
      const base = getBase(workspaceUuid, selectedBillingAccountId)
      if (!base) throw new Error('Workspace or billing account not selected')
      return fetchWrapper.post<PromptBookmark>(base, payload)
    },
    onSuccess: () => invalidateBookmarks(qc)
  })
}

export const useUpdatePromptBookmark = () => {
  const selectedBillingAccountId = useSelectedBillingAccountId()
  const workspaceUuid = useCurrentWorkspaceUuid(selectedBillingAccountId)
  const qc = useQueryClient()
  return useMutation<PromptBookmark, Error, { uuid: string; payload: UpdatePromptBookmarkPayload }>(
    {
      mutationFn: ({ uuid, payload }) => {
        const base = getBase(workspaceUuid, selectedBillingAccountId)
        if (!base) throw new Error('Workspace or billing account not selected')
        return fetchWrapper.patch<PromptBookmark>(`${base}/${uuid}`, payload)
      },
      onSuccess: () => invalidateBookmarks(qc)
    }
  )
}

export const useDeletePromptBookmark = () => {
  const selectedBillingAccountId = useSelectedBillingAccountId()
  const workspaceUuid = useCurrentWorkspaceUuid(selectedBillingAccountId)
  const qc = useQueryClient()
  return useMutation<void, Error, { uuid: string }>({
    mutationFn: ({ uuid }) => {
      const base = getBase(workspaceUuid, selectedBillingAccountId)
      if (!base) throw new Error('Workspace or billing account not selected')
      return fetchWrapper.delete(`${base}/${uuid}`)
    },
    onSuccess: () => invalidateBookmarks(qc)
  })
}
